// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { ButtonProps } from "@adl/MobiusOverrides/Button/Button";
import { color, layoutContainer, xScreen } from "@adl/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    buttonTitle = "buttonTitle",
    buttonLink = "buttonLink",
    image = "image",
    description = "description",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.image]: string;
        [fields.description]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonTitle]: string;
        [fields.backgroundColor]: string;
    };
}

type Props = OwnProps;

export interface AboutAquiferStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    imgWrapper?: InjectableCss;
    imgStyle?: ImgProps;
    contentWrapper?: InjectableCss;
    titleText?: TypographyProps;
    description?: TypographyProps;
    buttonStyle?: ButtonProps;
}
const flexBoxWrap = 1330;

const aboutAquiferStyles: AboutAquiferStyles = {
    wrapper: {
        css: css`
            background: ${({ backgroundColor }: any) => backgroundColor || `${color("common.ui9")}`};
            padding: 48px 0;
            width: 100%;
            ${xScreen("lte", "xs")} {
                padding: 16px 0;
            }
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            width: 100%;
            ${layoutContainer}
        `,
    },
    imgWrapper: {
        css: css`
            width: 530px;
            height: 270px;
            margin-right: 20px;
            ${xScreen("lte", flexBoxWrap)} {
                width: 100%;
                margin-right: 0;
            }
            ${xScreen("lte", "xs")} {
                height: auto;
                max-height: 270px;
            }
        `,
    },
    imgStyle: {
        css: css`
            height: auto;
            width: auto;
            max-width: 100%;
            max-height: 100%;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    contentWrapper: {
        css: css`
            max-width: 700px;
            ${xScreen("lte", flexBoxWrap)} {
                margin-top: 24px;
                max-width: unset;
            }
        `,
    },
    titleText: {
        variant: "h2",
    },
    description: {
        variant: "p",
        css: css`
            & > p:last-child {
                margin-bottom: 0;
            }
            margin-bottom: 24px;
        `,
    },
    buttonStyle: {
        sizeVariant: "medium",
    },
};

const styles = aboutAquiferStyles;

export const AboutAquifer: React.FunctionComponent<Props> = ({
    fields: { image, title, description, backgroundColor, buttonTitle, buttonLink },
}) => {
    const { url, title: linkTitle } = useGetLink(buttonLink);
    const history = useHistory();

    const handleButtonClick = () => {
        if (!url) {
            return;
        }
        history.push(url);
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.imgWrapper}>
                    <Img
                        {...styles.imgStyle}
                        src={image}
                        altText={title}
                        {...{
                            transformations: { width: 530 },
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.contentWrapper}>
                    <Typography {...styles.titleText}>{translate(title)}</Typography>
                    <Typography {...styles.description} as="div">
                        {parse(translate(description), parserOptions)}
                    </Typography>
                    <Button {...styles.buttonStyle} onClick={handleButtonClick}>
                        {translate(buttonTitle || linkTitle || "")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: AboutAquifer,
    definition: {
        group: "Xngage Custom" as any,
        icon: "Content",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },

            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.buttonTitle,
                editorTemplate: "TextField",
                displayName: "Button Title",
                defaultValue: "Meet the Aquifer Team",
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "rgba(247,243,236,1)",
            },
        ],
    },
};

export default widgetModule;
